import React, { useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiTrash2, FiShoppingBag, FiMinus, FiPlus } from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { useTelegram } from '../../hooks/useTelegram';

const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}`;

/* ─── Top-up card denominations per region ─── */
/* Only India has top-up cards. Turkey & Ukraine = manual service (9:00–23:45 MSK) */
const TOPUP_CARDS = {
  in: [
    { nominal: 1000, currency: 'INR', priceRub: 1100,  label: 'PSN India' },
    { nominal: 2000, currency: 'INR', priceRub: 2200,  label: 'PSN India' },
    { nominal: 3000, currency: 'INR', priceRub: 3300,  label: 'PSN India' },
    { nominal: 4000, currency: 'INR', priceRub: 4400,  label: 'PSN India' },
    { nominal: 5000, currency: 'INR', priceRub: 5500,  label: 'PSN India' },
  ],
};
/* Regions with manual service (working hours restriction) */
const MANUAL_REGIONS = new Set(['tr', 'ua']);

/* ─── Greedy algorithm: pick fewest cards to cover totalLocal ─── */
function calcOptimalCards(region, totalLocal) {
  const cards = TOPUP_CARDS[region];
  if (!cards || totalLocal <= 0) return {};
  const sorted = [...cards].sort((a, b) => b.nominal - a.nominal);
  let remaining = totalLocal;
  const result = {};
  for (const card of sorted) {
    if (remaining <= 0) break;
    const count = Math.floor(remaining / card.nominal);
    if (count > 0) {
      result[card.nominal] = count;
      remaining -= count * card.nominal;
    }
  }
  // If remainder, add one smallest card that covers it
  if (remaining > 0) {
    const cover = sorted.reverse().find(c => c.nominal >= remaining) || sorted[sorted.length - 1];
    result[cover.nominal] = (result[cover.nominal] || 0) + 1;
  }
  return result;
}

/* ─── Styled ─── */
const Page = styled.div`
  min-height: 100vh; background: #000; padding: 16px 16px 180px;
  animation: ${fadeIn} 0.3s ease;
`;
const Header = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;`;
const HeaderTitle = styled.h1`font-size: 28px; font-weight: 800; color: #fff; margin: 0;`;
const HeaderSum = styled.span`font-size: 20px; font-weight: 800; color: #30D158;`;

const EmptyWrap = styled.div`display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; text-align: center;`;
const EmptyIcon = styled.div`font-size: 56px; margin-bottom: 16px; opacity: 0.3;`;
const EmptyTitle = styled.h2`color: rgba(255,255,255,0.6); font-size: 18px; font-weight: 700; margin-bottom: 8px;`;
const EmptyText = styled.p`color: rgba(255,255,255,0.3); font-size: 14px; margin-bottom: 24px;`;
const EmptyBtn = styled.button`
  background: #006FFF; color: #fff; border: none; padding: 12px 28px; border-radius: 14px;
  font-size: 15px; font-weight: 600; cursor: pointer; &:active { opacity: 0.8; }
`;

const Section = styled.div`
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 18px; padding: 16px; margin-top: 16px;
`;
const SectionHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;`;
const SectionTitle = styled.div`font-size: 18px; font-weight: 800; color: #fff;`;
const SectionSum = styled.div`font-size: 16px; font-weight: 800; color: ${p => p.$color || '#30D158'};`;
const SectionHint = styled.div`font-size: 13px; color: rgba(255,255,255,0.35); line-height: 1.4; margin-bottom: 16px;`;

/* ─── Top-up card row ─── */
const CardRow = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 12px 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:last-of-type { border-bottom: none; }
`;
const CardBadge = styled.div`
  width: 64px; height: 44px; border-radius: 8px; flex-shrink: 0;
  background: linear-gradient(135deg, ${p => p.$bg || '#1a3a5c'}, ${p => p.$bg2 || '#0d1f33'});
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  position: relative; overflow: hidden;
`;
const CardBadgeLabel = styled.div`font-size: 7px; font-weight: 600; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.3px;`;
const CardBadgeNominal = styled.div`font-size: 13px; font-weight: 800; color: #fff;`;
const CardBadgeRegion = styled.div`
  position: absolute; bottom: 2px; right: 3px; font-size: 6px; font-weight: 600;
  color: rgba(255,255,255,0.4); display: flex; align-items: center; gap: 2px;
`;
const CardInfo = styled.div`flex: 1;`;
const CardPrice = styled.div`font-size: 15px; font-weight: 700; color: #fff;`;
const CardNominal = styled.div`font-size: 12px; color: rgba(255,255,255,0.35); margin-top: 1px;`;

const QtyControls = styled.div`display: flex; align-items: center; gap: 0;`;
const QtyBtn = styled.button`
  width: 36px; height: 36px; border-radius: 50%; border: 1.5px solid rgba(0,111,255,0.3);
  background: rgba(0,111,255,0.08); color: #006FFF; display: flex; align-items: center;
  justify-content: center; cursor: pointer; transition: all 0.15s;
  &:active { transform: scale(0.9); background: rgba(0,111,255,0.15); }
  &:disabled { opacity: 0.2; cursor: default; }
`;
const QtyNum = styled.div`
  width: 36px; text-align: center; font-size: 16px; font-weight: 700; color: #fff;
`;

/* ─── Game item in "Выбранные товары" ─── */
const GameRow = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 10px 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:last-of-type { border-bottom: none; }
`;
const GameImg = styled.img`
  width: 52px; height: 66px; border-radius: 10px; object-fit: cover; background: rgba(255,255,255,0.03);
`;
const GameInfo = styled.div`flex: 1; min-width: 0;`;
const GameTitle = styled.div`font-size: 15px; font-weight: 700; color: #fff; line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;`;
const GameLocal = styled.div`font-size: 13px; color: rgba(255,255,255,0.45); margin-top: 3px; font-weight: 500;`;
const GameDel = styled.button`
  background: rgba(255,59,48,0.08); border: 1px solid rgba(255,59,48,0.12); color: #ff3b30;
  width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center;
  justify-content: center; cursor: pointer; flex-shrink: 0;
  &:active { opacity: 0.6; }
`;

/* ─── Info box (green check) ─── */
const InfoBox = styled.div`
  background: rgba(48,209,88,0.06); border: 1px solid rgba(48,209,88,0.15);
  border-radius: 14px; padding: 12px 14px; margin-bottom: 12px;
  display: flex; align-items: flex-start; gap: 10px;
`;
const InfoIcon = styled.div`
  width: 24px; height: 24px; border-radius: 50%; background: #30D158; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; color: #fff; font-weight: 700;
`;
const InfoText = styled.div`font-size: 13px; color: rgba(48,209,88,0.85); line-height: 1.4;`;

/* ─── Warning ─── */
const WarningBanner = styled.div`
  background: rgba(255,204,0,0.08); border: 1px solid rgba(255,204,0,0.2);
  border-radius: 18px; padding: 14px 16px; margin-top: 16px;
  display: flex; align-items: flex-start; gap: 10px;
`;
const WarningIcon = styled.span`font-size: 20px; flex-shrink: 0;`;
const WarningText = styled.span`font-size: 13px; color: rgba(255,204,0,0.9); line-height: 1.4;`;

/* ─── Totals ─── */
const TotalRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; padding: 8px 0;
  font-size: ${p => p.$big ? '18px' : '15px'};
  font-weight: ${p => p.$big ? '800' : '600'};
  color: ${p => p.$big ? '#fff' : 'rgba(255,255,255,0.6)'};
  border-top: ${p => p.$big ? '1px solid rgba(255,255,255,0.06)' : 'none'};
  margin-top: ${p => p.$big ? '8px' : '0'};
  padding-top: ${p => p.$big ? '12px' : '8px'};
`;
const DiscountRow = styled.div`
  display: flex; justify-content: space-between; padding: 8px 0;
  font-size: 14px; font-weight: 600; color: #30D158;
`;

/* ─── Form ─── */
const Input = styled.input`
  width: 100%; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.06);
  border-radius: 14px; padding: 14px 16px; color: #fff; font-size: 15px; font-weight: 500;
  outline: none; box-sizing: border-box; transition: all 0.2s;
  &::placeholder { color: rgba(255,255,255,0.15); }
  &:focus { border-color: #006FFF; background: rgba(0,111,255,0.04); }
  margin-bottom: 14px;
`;
const Toggle = styled.label`
  display: flex; align-items: center; gap: 14px; cursor: pointer; margin-bottom: 16px;
  background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.08);
  border-radius: 14px; padding: 14px 16px;
  &:active { opacity: 0.7; }
`;
const ToggleSwitch = styled.div`
  width: 48px; height: 28px; border-radius: 14px; flex-shrink: 0;
  background: ${p => p.$on ? '#006FFF' : 'rgba(255,255,255,0.12)'};
  position: relative; transition: background 0.2s;
  &::after { content: ''; position: absolute; top: 2px; left: ${p => p.$on ? '22px' : '2px'};
    width: 24px; height: 24px; border-radius: 50%; background: #fff; transition: left 0.2s;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3); }
`;
const ToggleText = styled.span`font-size: 15px; font-weight: 600; color: rgba(255,255,255,0.85); flex: 1;`;
const Checkbox = styled.label`display: flex; align-items: flex-start; gap: 10px; cursor: pointer; margin-bottom: 16px;`;
const CheckBox = styled.div`
  width: 20px; height: 20px; border-radius: 6px; flex-shrink: 0; margin-top: 1px;
  border: 2px solid ${p => p.$checked ? '#006FFF' : 'rgba(255,255,255,0.2)'};
  background: ${p => p.$checked ? '#006FFF' : 'transparent'};
  display: flex; align-items: center; justify-content: center; transition: all 0.2s;
  &::after { content: '✓'; color: #fff; font-size: 12px; font-weight: 700; opacity: ${p => p.$checked ? 1 : 0}; }
`;
const CheckText = styled.span`font-size: 13px; color: rgba(255,255,255,0.55); line-height: 1.5;
  a { color: #006FFF; text-decoration: none; font-weight: 600; }`;

/* ─── Promo ─── */
const PromoRow = styled.div`display: flex; gap: 8px; margin-bottom: 14px;`;
const PromoInput = styled.input`
  flex: 1; min-width: 0; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.06);
  border-radius: 12px; padding: 11px 12px; color: #fff; font-size: 14px; font-weight: 600;
  outline: none; box-sizing: border-box; text-transform: uppercase; letter-spacing: 0.5px;
  &::placeholder { color: rgba(255,255,255,0.15); text-transform: none; letter-spacing: 0; font-weight: 400; font-size: 14px; }
  &:focus { border-color: #006FFF; }
`;
const PromoBtn = styled.button`
  background: ${p => p.$applied ? 'rgba(48,209,88,0.12)' : 'rgba(0,111,255,0.1)'};
  border: 1.5px solid ${p => p.$applied ? 'rgba(48,209,88,0.25)' : 'rgba(0,111,255,0.2)'};
  border-radius: 12px; padding: 0 14px; color: ${p => p.$applied ? '#30D158' : '#006FFF'};
  font-size: 13px; font-weight: 700; cursor: pointer; white-space: nowrap; flex-shrink: 0;
  transition: all 0.2s;
  &:active { transform: scale(0.95); opacity: 0.7; }
`;
const PromoStatus = styled.div`
  font-size: 13px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px;
  color: ${p => p.$error ? '#FF453A' : '#30D158'};
  background: ${p => p.$error ? 'rgba(255,69,58,0.08)' : 'rgba(48,209,88,0.08)'};
  padding: 10px 14px; border-radius: 12px;
`;

/* ─── Order button ─── */
const OrderBtn = styled.button`
  width: 100%;
  background: ${p => p.disabled ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, #30D158, #28a946)'};
  color: ${p => p.disabled ? 'rgba(255,255,255,0.2)' : '#fff'};
  border: none; padding: 16px; border-radius: 16px; font-size: 17px; font-weight: 700;
  cursor: ${p => p.disabled ? 'default' : 'pointer'};
  display: flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s;
  &:active { ${p => !p.disabled && 'transform: scale(0.98); opacity: 0.9;'} }
`;

/* ─── Confirmation ─── */
const Confirmation = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 80px 20px; text-align: center; animation: ${fadeIn} 0.3s ease;
`;
const ConfIcon = styled.div`font-size: 56px; margin-bottom: 16px;`;
const ConfTitle = styled.h2`color: #fff; font-size: 22px; font-weight: 800; margin-bottom: 8px;`;
const ConfOrder = styled.div`
  font-size: 16px; color: #30D158; font-weight: 700; margin-bottom: 8px;
  background: rgba(48,209,88,0.1); padding: 8px 20px; border-radius: 12px;
`;
const ConfText = styled.p`color: rgba(255,255,255,0.4); font-size: 14px; margin-bottom: 24px;`;

/* ─── Helpers ─── */
function getRegion(game) { return game.selectedRegion || 'tr'; }
function getCurrencySymbol(r) { return r === 'tr' ? 'TL' : r === 'ua' ? 'UAH' : 'INR'; }
function getPrice(game) {
  const region = getRegion(game);
  const p = game.prices?.[region];
  return { rub: game.selectedPrice || p?.price_rub || 0, local: p?.price_local || 0, currency: getCurrencySymbol(region), region };
}
function isWorkingHours() {
  const now = new Date();
  const msk = new Date(now.getTime() + 3 * 3600000);
  const total = msk.getUTCHours() * 60 + msk.getUTCMinutes();
  return total >= 540 && total <= 1425;
}
function pluralItems(n) { return n === 1 ? 'товар' : n < 5 ? 'товара' : 'товаров'; }

const BADGE_COLORS = {
  tr: ['#1a4a7a', '#0d2844'],
  ua: ['#2a4a2a', '#142814'],
  in: ['#4a3a1a', '#28200d'],
};

/* ─── Component ─── */
const Cart = () => {
  const { items, removeItem, clearCart } = useCart();
  const { tg } = useTelegram();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [needAccount, setNeedAccount] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoApplied, setPromoApplied] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [cardOverrides, setCardOverrides] = useState({});

  const haptic = (t = 'light') => {
    try { window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(t); } catch {}
  };

  /* ─── Separate PS Plus subscriptions (simple RUB price) ─── */
  const psplusItems = useMemo(() => items.filter(g => g.type === 'psplus'), [items]);
  const psplusTotalRub = useMemo(() => psplusItems.reduce((s, g) => s + (g.selectedPrice || 0), 0), [psplusItems]);

  /* ─── Group items by region (exclude psplus) ─── */
  const regionGroups = useMemo(() => {
    const groups = {};
    items.filter(g => g.type !== 'psplus').forEach(g => {
      const p = getPrice(g);
      if (!groups[p.region]) groups[p.region] = { items: [], totalLocal: 0, totalRub: 0, currency: p.currency };
      groups[p.region].items.push(g);
      groups[p.region].totalLocal += p.local;
      groups[p.region].totalRub += p.rub;
    });
    return groups;
  }, [items]);

  /* ─── Calculate cards per region with overrides ─── */
  const cardsByRegion = useMemo(() => {
    const result = {};
    Object.entries(regionGroups).forEach(([region, group]) => {
      const optimal = calcOptimalCards(region, group.totalLocal);
      // Apply overrides
      const overrideKey = region;
      const overrides = cardOverrides[overrideKey] || {};
      const merged = { ...optimal };
      Object.entries(overrides).forEach(([nom, qty]) => {
        merged[Number(nom)] = qty;
      });
      // Remove zeros
      Object.keys(merged).forEach(k => { if (merged[k] <= 0) delete merged[k]; });
      result[region] = merged;
    });
    return result;
  }, [regionGroups, cardOverrides]);

  /* ─── Compute total cards cost ─── */
  const cardsTotalRub = useMemo(() => {
    let total = 0;
    Object.entries(cardsByRegion).forEach(([region, cards]) => {
      const regionCards = TOPUP_CARDS[region] || [];
      Object.entries(cards).forEach(([nom, qty]) => {
        const card = regionCards.find(c => c.nominal === Number(nom));
        if (card) total += card.priceRub * qty;
      });
    });
    return total;
  }, [cardsByRegion]);

  const cardsLocalTotal = useMemo(() => {
    const result = {};
    Object.entries(cardsByRegion).forEach(([region, cards]) => {
      if (MANUAL_REGIONS.has(region)) return; // skip manual regions
      const cur = getCurrencySymbol(region);
      if (!result[cur]) result[cur] = 0;
      Object.entries(cards).forEach(([nom, qty]) => {
        result[cur] += Number(nom) * qty;
      });
    });
    return result;
  }, [cardsByRegion]);

  /* Manual regions: price = game RUB price directly. Card regions: price = cards cost */
  const manualRub = useMemo(() => {
    return Object.entries(regionGroups)
      .filter(([r]) => MANUAL_REGIONS.has(r))
      .reduce((s, [, g]) => s + g.totalRub, 0);
  }, [regionGroups]);

  const subtotalRub = cardsTotalRub + manualRub + psplusTotalRub;
  const discountAmount = promoDiscount > 0 ? Math.round(subtotalRub * promoDiscount / 100) : 0;
  const totalRub = subtotalRub - discountAmount;

  const updateCardQty = (region, nominal, delta) => {
    haptic();
    setCardOverrides(prev => {
      const regionKey = region;
      const current = prev[regionKey] || {};
      const optimal = calcOptimalCards(region, regionGroups[region]?.totalLocal || 0);
      const currentQty = current[nominal] !== undefined ? current[nominal] : (optimal[nominal] || 0);
      const newQty = Math.max(0, currentQty + delta);
      return { ...prev, [regionKey]: { ...current, [nominal]: newQty } };
    });
  };

  const getCardQty = (region, nominal) => {
    const overrides = cardOverrides[region] || {};
    if (overrides[nominal] !== undefined) return overrides[nominal];
    return cardsByRegion[region]?.[nominal] || 0;
  };

  const checkPromo = async () => {
    if (!promoCode.trim()) return;
    haptic(); setPromoLoading(true); setPromoError('');
    try {
      const res = await fetch('/api/promo/check', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode })
      });
      const data = await res.json();
      if (data.valid) { setPromoDiscount(data.discount); setPromoApplied(data.code); setPromoError(''); haptic('success'); }
      else { setPromoDiscount(0); setPromoApplied(''); setPromoError(data.error || 'Неверный промокод'); haptic('error'); }
    } catch { setPromoError('Ошибка проверки'); }
    setPromoLoading(false);
  };

  const removePromo = () => { haptic(); setPromoCode(''); setPromoDiscount(0); setPromoApplied(''); setPromoError(''); };

  const hasRestricted = items.some(g => MANUAL_REGIONS.has(getRegion(g)));
  const working = isWorkingHours();
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canOrder = agreed && items.length > 0 && validEmail;

  const [orderLoading, setOrderLoading] = useState(false);

  const handleOrder = async () => {
    if (!canOrder || orderLoading) return;
    haptic('medium');
    setOrderLoading(true);
    try {
      const telegramId = tg?.initDataUnsafe?.user?.id || null;
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map(g => { const p = getPrice(g); return { id: g.id || g.sony_id, title: g.title, image_url: g.image_url, priceRub: p.rub, priceLocal: p.local, currency: p.currency, region: p.region }; }),
          cards: cardsByRegion,
          subtotalRub, promoCode: promoApplied || null, promoDiscount: promoDiscount || 0,
          discountAmount, totalRub, email, needAccount,
          telegramId,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        clearCart();
        haptic('success');
        // Close webapp so user sees the bot notification
        if (tg?.close) { setTimeout(() => tg.close(), 500); }
        else { setConfirmation(data.orderNumber); }
      } else {
        haptic('error');
        alert(data.error || 'Ошибка оформления');
      }
    } catch (e) {
      haptic('error');
      alert('Ошибка соединения');
    }
    setOrderLoading(false);
  };

  if (confirmation) {
    return (
      <Page>
        <Confirmation>
          <ConfIcon>✅</ConfIcon>
          <ConfTitle>Заказ оформлен!</ConfTitle>
          <ConfOrder>{confirmation}</ConfOrder>
          <ConfText>Мы свяжемся с вами в ближайшее время</ConfText>
          <EmptyBtn onClick={() => navigate('/catalog')}>Вернуться в каталог</EmptyBtn>
        </Confirmation>
      </Page>
    );
  }

  if (items.length === 0) {
    return (
      <Page>
        <Header><HeaderTitle>Корзина</HeaderTitle></Header>
        <EmptyWrap>
          <EmptyIcon>🛒</EmptyIcon>
          <EmptyTitle>Корзина пуста</EmptyTitle>
          <EmptyText>Добавляйте игры из каталога</EmptyText>
          <EmptyBtn onClick={() => navigate('/catalog')}>Перейти в каталог</EmptyBtn>
        </EmptyWrap>
      </Page>
    );
  }

  const regionNames = { tr: 'Турция', ua: 'Украина', in: 'Индия' };

  return (
    <Page>
      <Header>
        <HeaderTitle>Корзина</HeaderTitle>
        <HeaderSum>{totalRub.toLocaleString('ru')} ₽</HeaderSum>
      </Header>

      {/* ─── PS PLUS SUBSCRIPTIONS: simple RUB price ─── */}
      {psplusItems.length > 0 && (
        <Section>
          <SectionHeader>
            <SectionTitle>Подписки</SectionTitle>
            <SectionSum $color="#006FFF">{psplusTotalRub.toLocaleString('ru')} ₽</SectionSum>
          </SectionHeader>

          {psplusItems.map(item => (
            <GameRow key={item.id}>
              <GameImg src={item.image_url || item.image} alt={item.title} />
              <GameInfo>
                <GameTitle>{item.title}</GameTitle>
                <GameLocal>{item.subtitle} • {(item.selectedPrice || 0).toLocaleString('ru')} ₽</GameLocal>
              </GameInfo>
              <GameDel onClick={() => { haptic(); removeItem(item.id); }}>
                <FiTrash2 size={16} />
              </GameDel>
            </GameRow>
          ))}
        </Section>
      )}

      {/* ─── MANUAL REGIONS (TR/UA): combined into one block ─── */}
      {(() => {
        const manualItems = items.filter(g => g.type !== 'psplus' && MANUAL_REGIONS.has(getRegion(g)));
        if (manualItems.length === 0) return null;
        const manualTotal = manualItems.reduce((s, g) => s + getPrice(g).rub, 0);
        return (
          <Section>
            <SectionHeader>
              <SectionTitle>Оформление заказа</SectionTitle>
              <SectionSum $color="#006FFF">{manualTotal.toLocaleString('ru')} ₽</SectionSum>
            </SectionHeader>

            <InfoBox style={{marginBottom: 12}}>
              <InfoIcon>✓</InfoIcon>
              <InfoText>Мы оформим покупку на ваш аккаунт вручную. Время работы операторов оформления: ежедневно с 9:00 до 23:45 по Московскому времени.</InfoText>
            </InfoBox>

            {manualItems.map(game => {
              const p = getPrice(game);
              return (
                <GameRow key={game.id || game.sony_id}>
                  <GameImg src={game.image_url} alt={game.title} />
                  <GameInfo>
                    <GameTitle>{game.title}</GameTitle>
                    <GameLocal>{p.local.toLocaleString('ru')} {p.currency} • {p.rub.toLocaleString('ru')} ₽</GameLocal>
                  </GameInfo>
                  <GameDel onClick={() => { haptic(); removeItem(game.id || game.sony_id); }}>
                    <FiTrash2 size={16} />
                  </GameDel>
                </GameRow>
              );
            })}
          </Section>
        );
      })()}

      {/* ─── CARD REGIONS (IN): top-up cards + selected games ─── */}
      {Object.entries(regionGroups).filter(([r]) => !MANUAL_REGIONS.has(r)).map(([region, group]) => {
        const cards = TOPUP_CARDS[region] || [];
        const localSum = cardsLocalTotal[group.currency] || 0;
        const colors = BADGE_COLORS[region] || BADGE_COLORS.in;
        const surplus = localSum - group.totalLocal;
        const enough = surplus >= 0;

        return (
          <React.Fragment key={`card-region-${region}`}>
            <Section>
              <SectionHeader>
                <SectionTitle>Карты для пополнения</SectionTitle>
                <SectionSum $color="#30D158">{localSum.toLocaleString('ru')} {group.currency}</SectionSum>
              </SectionHeader>
              <SectionHint>
                {enough
                  ? `Лучший набор карт для оплаты выбранных товаров (${regionNames[region]}).`
                  : `Недостаточно карт. Нужно минимум ${group.totalLocal.toLocaleString('ru')} ${group.currency}.`
                }
                {' '}Номиналов ниже {cards[0]?.nominal} {group.currency} нет.
              </SectionHint>

              {cards.map(card => {
                const qty = getCardQty(region, card.nominal);
                if (qty === 0 && !cardsByRegion[region]?.[card.nominal]) return null;
                return (
                  <CardRow key={card.nominal}>
                    <CardBadge $bg={colors[0]} $bg2={colors[1]}>
                      <CardBadgeLabel>КАРТА</CardBadgeLabel>
                      <CardBadgeNominal>{card.nominal.toLocaleString('ru')} {card.currency}</CardBadgeNominal>
                      <CardBadgeRegion>🎮 {card.label}</CardBadgeRegion>
                    </CardBadge>
                    <CardInfo>
                      <CardPrice>{(card.priceRub * Math.max(1, qty)).toLocaleString('ru')} ₽</CardPrice>
                      <CardNominal>Номинал {card.nominal.toLocaleString('ru')} {card.currency}</CardNominal>
                    </CardInfo>
                    <QtyControls>
                      <QtyBtn onClick={() => updateCardQty(region, card.nominal, -1)} disabled={qty <= 0}><FiMinus size={16} /></QtyBtn>
                      <QtyNum>{qty}</QtyNum>
                      <QtyBtn onClick={() => updateCardQty(region, card.nominal, 1)}><FiPlus size={16} /></QtyBtn>
                    </QtyControls>
                  </CardRow>
                );
              })}
            </Section>

            <Section>
              <SectionHeader>
                <SectionTitle>Выбранные товары</SectionTitle>
                <SectionSum $color={enough ? '#30D158' : '#FF453A'}>{group.totalLocal.toLocaleString('ru')} {group.currency}</SectionSum>
              </SectionHeader>
              <SectionHint>
                {enough
                  ? `Обойдутся в ${group.totalLocal.toLocaleString('ru')} ${group.currency}.${surplus > 0 ? ` Непотраченные ${surplus.toLocaleString('ru')} ${group.currency} останутся на аккаунте.` : ''}`
                  : `Не хватает ${Math.abs(surplus).toLocaleString('ru')} ${group.currency}. Добавьте карт.`}
              </SectionHint>

              {enough && (
                <InfoBox>
                  <InfoIcon>✓</InfoIcon>
                  <InfoText>Вы покупаете карты на {localSum.toLocaleString('ru')} {group.currency}. Этого хватит для оплаты в PS Store.</InfoText>
                </InfoBox>
              )}

              {group.items.map(game => {
                const p = getPrice(game);
                return (
                  <GameRow key={game.id || game.sony_id}>
                    <GameImg src={game.image_url} alt={game.title} />
                    <GameInfo>
                      <GameTitle>{game.title}</GameTitle>
                      <GameLocal>Цена в PS Store {p.local.toLocaleString('ru')} {p.currency}</GameLocal>
                    </GameInfo>
                    <GameDel onClick={() => { haptic(); removeItem(game.id || game.sony_id); }}>
                      <FiTrash2 size={16} />
                    </GameDel>
                  </GameRow>
                );
              })}
            </Section>
          </React.Fragment>
        );
      })}

      {/* ─── Promo ─── */}
      <Section>
        <SectionTitle style={{fontSize:14,color:'rgba(255,255,255,0.5)',fontWeight:600}}>Промокод</SectionTitle>
        {promoApplied ? (
          <>
            <PromoStatus>✅ Промокод <b>{promoApplied}</b> — скидка {promoDiscount}%</PromoStatus>
            <PromoBtn $applied onClick={removePromo} style={{width:'100%',padding:'10px',textAlign:'center',display:'block'}}>Удалить промокод</PromoBtn>
          </>
        ) : (
          <>
            <PromoRow>
              <PromoInput placeholder="Введите промокод" value={promoCode}
                onChange={e => setPromoCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && checkPromo()} />
              <PromoBtn onClick={checkPromo} disabled={promoLoading}>
                {promoLoading ? '...' : 'Применить'}
              </PromoBtn>
            </PromoRow>
            {promoError && <PromoStatus $error>❌ {promoError}</PromoStatus>}
          </>
        )}
      </Section>

      {/* ─── Totals ─── */}
      <Section>
        <SectionTitle style={{fontSize:14,color:'rgba(255,255,255,0.5)',fontWeight:600}}>Итого</SectionTitle>
        <TotalRow>
          <span>{items.length} {pluralItems(items.length)}</span>
          <span>{subtotalRub.toLocaleString('ru')} ₽</span>
        </TotalRow>
        {Object.entries(cardsLocalTotal).map(([cur, val]) => (
          <TotalRow key={cur}>
            <span>Карты пополнения</span>
            <span>{val.toLocaleString('ru')} {cur}</span>
          </TotalRow>
        ))}
        {discountAmount > 0 && (
          <DiscountRow>
            <span>Скидка {promoDiscount}%</span>
            <span>−{discountAmount.toLocaleString('ru')} ₽</span>
          </DiscountRow>
        )}
        <TotalRow $big>
          <span>К оплате</span>
          <span>{totalRub.toLocaleString('ru')} ₽</span>
        </TotalRow>
      </Section>

      {/* ─── Checkout ─── */}
      <Section>
        <SectionTitle style={{fontSize:16,color:'#fff',fontWeight:700}}>Перед оплатой</SectionTitle>
        <Input type="email" placeholder="E-mail для чека" value={email} onChange={e => setEmail(e.target.value)} />
        <Toggle onClick={() => { haptic(); setNeedAccount(!needAccount); }}>
          <ToggleText>Мне нужен новый аккаунт PS Store</ToggleText>
          <ToggleSwitch $on={needAccount} />
        </Toggle>
        <Checkbox onClick={() => { haptic(); setAgreed(!agreed); }}>
          <CheckBox $checked={agreed} />
          <CheckText>
            Согласен с условиями <a href="https://пс-стор.рф/oferta" target="_blank" rel="noreferrer">Пользовательского соглашения</a> и <a href="https://пс-стор.рф/privacy" target="_blank" rel="noreferrer">Политики конфиденциальности</a>
          </CheckText>
        </Checkbox>
        <OrderBtn disabled={!canOrder || orderLoading} onClick={handleOrder}>
          <FiShoppingBag size={18} />
          {orderLoading ? 'Оформляем...' : 'Оформить заказ'}
        </OrderBtn>
      </Section>
    </Page>
  );
};

export default Cart;
